import React, { useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { DialogMode } from '../type/DialogMode';
import { Enterprise, Notice, PlacementType, User } from '../type';
import { CloseButton } from './CloseButton';
import { CancelButton } from './CancelButton';
import { uploadImage } from '../service/imageService';
import { Timestamp } from '@firebase/firestore';
import { uploadHtml } from '../service/htmlService';
import { sortedUsers, waitForHtmlContentUrl } from '../utils/util';
import { renderEnterpriseTree } from '../hook/useEnterpriseTree';

interface NoticeDialogProps {
  open: boolean;
  mode: DialogMode;
  notice?: Notice;
  onClose: () => void;
  onSave: (notice: Notice) => void;
  onDelete?: (noticeId: string) => void;
  i_users: User[];
  e_users: User[];
  ent: Enterprise[];
}

const NoticeDialog: React.FC<NoticeDialogProps> = React.memo(({
  open,
  mode,
  notice,
  onClose,
  onSave,
  onDelete,
  i_users,
  e_users,
  ent,
}) => {
  const [title, setTitle] = useState('');
  const [contentUrl, setContentUrl] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [bannerHtmlZipUrl, setBannerHtmlZipUrl] = useState('');
  const [bannerHtmlZipFile, setBannerHtmlZipFile] = useState<File | null>(null);
  const [htmlContentUrl, setHtmlContentUrl] = useState<string | null>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [selectedIndependentUsers, setSelectedIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterpriseUsers, setSelectedEnterpriseUsers] = useState<User[]>([]);
  const [uploadType, setUploadType] = useState<'zip' | 'url'>('zip');
  const [isPopup, setIsPopup] = useState(false);
  const [placement, setPlacement] = useState<PlacementType | ''>(PlacementType.NONE);
  const [order, setOrder] = useState(0);
  
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Set initial values when Dialog opens
  React.useEffect(() => {
    if (open) {
      if (mode === DialogMode.EDIT && notice) {
        setTitle(notice.title);
        setUploadType(notice.uploadType ?? 'zip');
        if (notice.uploadType === 'zip') {
          setHtmlContentUrl(notice.content ?? '');
        } else { 
          setContentUrl(notice.content ?? '');
        }
        setIsPublic(notice.isPublic !== false);
        setIsPopup(notice.isPopup || false); 
        setPlacement(notice.placement ? notice.placement : PlacementType.NONE);
        setBannerImageUrl(notice.bannerImageUrl || '');
        setEnterprises(ent);
        const tempSelectedUsers = notice.allowedUsers ? i_users.filter(u => notice.allowedUsers?.includes(u.uid) && !u.isEnterpriseUser) : [];
        const tempUsers = notice.allowedUsers ? 
                          i_users.filter(u => !notice.allowedUsers?.includes(u.uid)) : 
                          tempSelectedUsers?.length > 0 ? i_users.filter(u => tempSelectedUsers.some(selected => selected.uid !== u.uid)) : i_users;
        setSelectedIndependentUsers(sortedUsers(tempSelectedUsers));
        setIndependentUsers(sortedUsers(tempUsers));

        const tempSelectedEnterpriseUsers = [...(notice.allowedEnterprises ? e_users.filter(u => u.enterpriseId && notice.allowedEnterprises?.includes(u.enterpriseId)) : []), ...(e_users.filter(u => notice.allowedUsers?.includes(u.uid) && u.isEnterpriseUser))];
        const tempEnterpriseUsers = notice.allowedEnterprises ? 
                                    e_users.filter(u => u.enterpriseId && !notice.allowedEnterprises?.includes(u.enterpriseId) && !notice.allowedUsers?.includes(u.uid)) : 
                                    tempSelectedEnterpriseUsers?.length > 0 ? e_users.filter(u => tempSelectedEnterpriseUsers.some(selected => selected.uid !== u.uid)) : e_users;
        setSelectedEnterpriseUsers(sortedUsers(tempSelectedEnterpriseUsers));
        setEnterpriseUsers(sortedUsers(tempEnterpriseUsers));
      } else {
        setTitle('');
        setUploadType('zip');
        setContentUrl('');
        setHtmlContentUrl('');
        setIsPublic(true);
        setIsPopup(false);
        setPlacement(PlacementType.NONE);
        setBannerImageUrl('');
        setBannerImageFile(null);
        setBannerHtmlZipUrl('');
        setBannerHtmlZipFile(null);
        setSelectedIndependentUsers([]);
        setSelectedEnterpriseUsers([]);
        if (mode === DialogMode.CREATE) {
          setEnterprises(ent);
          const tempSelectedUsers = i_users.filter(u => !u.isEnterpriseUser);
          const tempUsers = tempSelectedUsers?.length > 0 ? i_users.filter(u => tempSelectedUsers.some(selected => selected.uid !== u.uid)) : i_users;
          setIndependentUsers(sortedUsers(tempUsers));

          const tempSelectedEnterpriseUsers = [...(e_users.filter(u => u.enterpriseId)), ...(e_users.filter(u => u.isEnterpriseUser))];
          const tempEnterpriseUsers = tempSelectedEnterpriseUsers?.length > 0 ? e_users.filter(u => tempSelectedEnterpriseUsers.some(selected => selected.uid !== u.uid)) : e_users;
          setEnterpriseUsers(sortedUsers(tempEnterpriseUsers));
        } else {
          setEnterprises([]);
          setIndependentUsers([]);
          setEnterpriseUsers([]);
        }
      }
    }
  }, [open, mode, notice]);

  React.useEffect(() => {
        console.log("selectedIndependentUsers changed:", selectedIndependentUsers);
  }, [selectedIndependentUsers]);
  React.useEffect(() => {
        console.log("independentUsers changed:", independentUsers);
  }, [independentUsers]);

  const handleClose = useCallback(() => {
    setTitle('');
    setContentUrl('');
    setHtmlContentUrl('');
    setIsPublic(true);
    setIsPopup(false);
    setPlacement(PlacementType.NONE);
    setBannerImageUrl('');
    setBannerImageFile(null);
    setBannerHtmlZipUrl('');
    setBannerHtmlZipFile(null);
    setUploadError(null);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {

    function isEnterpriseFullySelected(enterpriseId: string) {
      const remaining = enterpriseUsers.filter(user => user.enterpriseId === enterpriseId);
      return remaining.length === 0;
    }

    setUploadError(null);
    
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    if (!bannerImageFile && !bannerImageUrl) {
      alert('배너 이미지를 선택해주세요.');
      return;
    }
    let finalBannerImageUrl = bannerImageUrl;    
      
    // 배너 이미지 업로드가 필요한 경우
    if (bannerImageFile) {
      try {
        setIsUploading(true);
        finalBannerImageUrl = await uploadImage(bannerImageFile, 'notice-banners');
      } catch (error: any) {
        setUploadError('배너 이미지 업로드에 실패했습니다. 다시 시도해주세요.');
        return;
      } finally {
        setIsUploading(false);
      }
    }

    let finalHtmlContentUrl: string | null = null;
    if (uploadType === 'zip') {
      finalHtmlContentUrl = htmlContentUrl;
      if (bannerHtmlZipFile) {
        try {
          setIsUploading(true);
          const { folderName } = await uploadHtml(bannerHtmlZipFile, 'notice-banners');

          // ✅ wait until Firestore doc is ready
          finalHtmlContentUrl = await waitForHtmlContentUrl(folderName);
        } catch (error: any) {
          console.log(error);
          setUploadError('배너 HTML 업로드에 실패했습니다. 다시 시도해주세요.');
          return;
        } finally {
          setIsUploading(false);
        }
      }
    } else {
      finalHtmlContentUrl = contentUrl.trim();
    }
  
    // 간단한 HTML 생성
    const allowedEnterprises: string[] = [];
    const tmpEnterpriseUsers = isPublic ? [] : [...selectedEnterpriseUsers];
    for (let i = tmpEnterpriseUsers.length - 1; i >= 0; i--) {
      const user = tmpEnterpriseUsers[i];
      if (user.enterpriseId) {
        const fullySelected = isEnterpriseFullySelected(user.enterpriseId!);
        if (fullySelected) {
          // 해당 기업의 ID를 추가
          if (!allowedEnterprises.includes(user.enterpriseId)) {
            allowedEnterprises.push(user.enterpriseId);
          }
          tmpEnterpriseUsers.splice(i, 1); // 기업 사용자 목록에서 제거
        }
      } else {
        tmpEnterpriseUsers.splice(i, 1); // 기업 사용자 목록에서 제거
      }
    }
    const allowedUsers = isPublic ? [] : [...selectedIndependentUsers, ...tmpEnterpriseUsers].map(u => u.uid);

    const noticeDoc: Notice = {
      noticeId: mode === DialogMode.EDIT && notice ? notice.noticeId : `notice_${Date.now()}`,
      title: title.trim(),
      uploadType: uploadType,
      content: finalHtmlContentUrl ?? "",
      bannerImageUrl: finalBannerImageUrl,
      isPublic,
      placement: placement ? placement : PlacementType.NONE,
      order: placement && placement !== PlacementType.NONE ? order : 0,
      isPopup: isPopup,
      allowedUsers,
      allowedEnterprises,
      createdAt: mode === DialogMode.CREATE ? Timestamp.now() : notice!.createdAt,
    };
    
    onSave(noticeDoc);
    handleClose();
  }, [title, htmlContentUrl, placement, order, isPopup, uploadType, contentUrl, handleClose, enterpriseUsers, bannerHtmlZipFile, bannerImageUrl, bannerImageFile, isPublic, mode, notice, onSave, selectedIndependentUsers, selectedEnterpriseUsers]);
  
  const handleDelete = useCallback(() => {
    if (mode === DialogMode.EDIT && notice?.noticeId && onDelete) {
      onDelete(notice.noticeId);
      handleClose();
    }
  }, [mode, notice, onDelete, handleClose]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handlePublicChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPublic(e.target.checked);
  }, []);

  const handleBannerImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerImageFile(file);
      // 미리보기 URL 생성
      const reader = new FileReader();
      reader.onload = (event) => {
        setBannerImageUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleBannerImageClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const getDialogTitle = () => {
    return mode === DialogMode.CREATE ? '새 공지사항 작성' : '공지사항 수정';
  };

  const getSaveButtonText = () => {
    return mode === DialogMode.CREATE ? '저장' : '수정';
  };

  const handleHtmlZipChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerHtmlZipFile(file);
      // 미리보기 URL 생성
      const reader = new FileReader();
      reader.onload = (event) => {
        setBannerHtmlZipUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleZipClick = () => {
    zipInputRef.current?.click();
  };

  const handlePopupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPopup(e.target.checked);
  };

  const handlePlacementChange = (e: SelectChangeEvent) => {
    setPlacement(e.target.value as PlacementType ?? PlacementType.NONE);
  };
  
  const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrder(Number(e.target.value));
  };
  /*
  const renderEnterpriseTree = (
    users: User[],
    isSelectedTree: boolean
  ) => {
    const grouped = users.reduce((acc, user) => {
      const eid = user.enterpriseId!;
      if (!acc[eid]) acc[eid] = [];
      acc[eid].push(user);
      return acc;
    }, {} as Record<string, User[]>);

    return (
      <SimpleTreeView>
        {Object.entries(grouped).map(([enterpriseId, members]) => (
          <TreeItem
            itemId={`enterprise-${enterpriseId}`} 
            // label={enterprises.find(e => e.enterpriseId === enterpriseId)?.enterpriseName} key={enterpriseId}
            label={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>{enterprises.find(e => e.enterpriseId === enterpriseId)?.enterpriseName}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: '60px', padding: '4px 8px' }}
                  onClick={() => {
                    const enterpriseUsersToMove = members;
                    if (isSelectedTree) {
                      setSelectedEnterpriseUsers(prev =>
                        sortedUsers(prev.filter(u => u.enterpriseId !== enterpriseId))
                      );
                      setEnterpriseUsers(prev => sortedUsers([...prev, ...enterpriseUsersToMove]));
                    } else {
                      setEnterpriseUsers(prev =>
                        sortedUsers(prev.filter(u => u.enterpriseId !== enterpriseId))
                      );
                      setSelectedEnterpriseUsers(prev => sortedUsers([...prev, ...enterpriseUsersToMove]));
                    }
                  }}
                >
                  {isSelectedTree ? '기업 미선택' : '기업 선택'}
                </Button>
              </Box>
            }
          >
            {members.map(user => (
              <TreeItem
                itemId={`user-${user.uid}`}
                label={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography>{user.uid}</Typography>
                    <Typography>{user.displayName}</Typography>
                    <Typography>{user.phoneNumber}</Typography>
                    <Button
                      size="small"
                      sx={{ minWidth: '60px', padding: '4px 8px' }}
                      onClick={() => {
                        if (isSelectedTree) {
                          setSelectedEnterpriseUsers(sortedUsers(selectedEnterpriseUsers.filter(u => u.uid !== user.uid)));
                          setEnterpriseUsers(sortedUsers([...enterpriseUsers, user]));
                        } else {
                          setEnterpriseUsers(sortedUsers(enterpriseUsers.filter(u => u.uid !== user.uid)));
                          setSelectedEnterpriseUsers(sortedUsers([...selectedEnterpriseUsers, user]));
                        }
                    }}>
                      {isSelectedTree ? '미선택' : '선택'}
                    </Button>
                  </Box>
                }
                key={user.uid}
              />
            ))}
          </TreeItem>
        ))}
      </SimpleTreeView>
    );
  };
*/
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >

      {/* Top close button and title */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>
          {getDialogTitle()}
        </Typography>
        <CloseButton onClose={onClose} />
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* 왼쪽 컬럼: 제목, 배너 이미지, 업로드 타입 선택 */}
          <Box
            sx={{
              flex: 0.9,
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              mt: 0.5,
              border: '1px solid #ccc',
              borderRadius: 2,
              padding: 2,
          }}>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {uploadError}
              </Alert>
            )}
            <TextField
              label="제목"
              value={title}
              onChange={handleTitleChange}
              fullWidth
            />

            {/* 배너 이미지 업로드 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Alert severity="info">
                배너 이미지를 업로드하면 공지사항 목록에서 배너로 표시됩니다.
              </Alert>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#999',
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={handleBannerImageClick}
              >
                {bannerImageUrl ? (
                  <img 
                    src={bannerImageUrl} 
                    alt="배너 미리보기" 
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    배너 이미지를 클릭하여 업로드하세요
                  </Typography>
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleBannerImageChange}
                style={{ display: 'none' }}
              />
            </Box>

            <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />

            {/* HTML 업로드 타입 선택 및 업로드 */}
            <FormControl component="fieldset">
              <FormLabel component="legend">배너 콘텐츠 형식 선택 (배너 이미지 클릭 시 표시됩니다)</FormLabel>
              <RadioGroup
                row
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value as 'zip' | 'url')}
              >
                <FormControlLabel value="zip" control={<Radio />} label="ZIP 파일 업로드" />
                <FormControlLabel value="url" control={<Radio />} label="URL 입력" />
              </RadioGroup>
            </FormControl>
            {uploadType === 'url' ? (
              <TextField
                label="공지사항 콘텐츠 URL"
                value={contentUrl}
                onChange={(e) => setContentUrl(e.target.value)}
                fullWidth
                placeholder="https://example.com/banner.jpg"
              /> 
              ) : (
              <Box sx={{ mb: 2 }}>
                {bannerHtmlZipFile && (
                  <div className="mt-2 p-2 border rounded bg-gray-50">
                    <p>📦 {bannerHtmlZipFile.name}</p>
                    <p className="text-sm text-gray-600">
                      {(bannerHtmlZipFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                )}
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#999',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  onClick={handleZipClick}
                >
                  <Typography variant="body2" color="textSecondary">
                    ZIP 파일을 업로드하여 HTML을 표시하세요
                  </Typography>
                  <input
                    ref={zipInputRef}
                    type="file"
                    accept=".zip"
                    onChange={handleHtmlZipChange}
                    style={{ display: 'none' }}
                  />
                </Box>
                {htmlContentUrl && (
                  <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
                    <iframe
                      ref={iframeRef}
                      src={htmlContentUrl}
                      style={{ width: "100%", height: "400px", border: "none" }}
                      title="공지사항 미리보기"
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* 오른쪽 컬럼: 공개 여부 및 사용자 선택 */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {/* Additional Checkbox */}
              <Box sx={{ minWidth: 160, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPopup}
                      onChange={handlePopupChange}
                    />
                }
                label="팝업 표시"
              />
              </Box>

              {/* Dropdown Menu */}
              <FormControl fullWidth>
                <InputLabel id="placement-label">공개위치</InputLabel>
                <Select
                  labelId="placement-label"
                  value={placement}
                  label="공개위치"
                  onChange={handlePlacementChange}
                >
                  <MenuItem value='none'>위치 없음</MenuItem>
                  <MenuItem value="top_banner">상단 배너</MenuItem>
                  <MenuItem value="bottom_banner">하단 배너</MenuItem>
                </Select>
              </FormControl>

              {/* 순서 Input Field */}
              <TextField
                label="순서"
                type="number"
                value={order ? order : 0}
                onChange={handleOrderChange}
                disabled={placement === 'none'}
                fullWidth
              />
            </Box>

            <Divider />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublic}
                  onChange={handlePublicChange}
                />
              }
              label="전체 공개"
            />

            {isPublic ? (
              <Typography variant="body2" color="textSecondary">
                모든 사용자가 이 공지를 볼 수 있습니다.
              </Typography>
            ) : (
              <>
              <Typography variant="body2" color="textSecondary">
                공지 볼 수 있는 사용자를 선택하세요.
              </Typography>
              <Box
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3, 
                  mt: 3,
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  padding: 2,
                }}>
                {/* 미선택 사용자 */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 개인 사용자</Typography>

                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setSelectedIndependentUsers(prev => sortedUsers([...prev, ...independentUsers]));
                        setIndependentUsers([]);
                      }}
                    >
                      전체 선택
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {independentUsers.map(user => (
                        <TableRow key={user.uid}>
                          <TableCell>{user.uid}</TableCell>
                          <TableCell>{user.displayName}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              sx={{ minWidth: '60px', padding: '4px 8px' }}
                              onClick={() => {
                                setIndependentUsers(sortedUsers(independentUsers.filter(u => u.uid !== user.uid)));
                                setSelectedIndependentUsers(sortedUsers([...selectedIndependentUsers, user]));
                            }}>선택</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />

                {/* 선택된 사용자 */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 개인 사용자</Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setIndependentUsers(prev => sortedUsers([...prev, ...selectedIndependentUsers]));
                        setSelectedIndependentUsers([]);
                      }}
                    >
                      전체 미선택
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {selectedIndependentUsers.map(user => (
                        <TableRow key={user.uid}>
                          <TableCell>{user.uid}</TableCell>
                          <TableCell>{user.displayName}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              sx={{ minWidth: '60px', padding: '4px 8px' }}
                              onClick={() => {
                                setSelectedIndependentUsers(sortedUsers(selectedIndependentUsers.filter(u => u.uid !== user.uid)));
                                setIndependentUsers(sortedUsers([...independentUsers, user]));
                            }}>미선택</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3, 
                  mt: 3,
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  padding: 2,
                }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 기업 사용자</Typography>
                  {/* {renderEnterpriseTree(enterpriseUsers, false)} */}
                  {renderEnterpriseTree(enterpriseUsers, enterprises, false, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers)}
                </Box>

                <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 기업 사용자</Typography>
                  {/* {renderEnterpriseTree(selectedEnterpriseUsers, true)} */}
                  {renderEnterpriseTree(selectedEnterpriseUsers, enterprises, true, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers)}
                </Box>
              </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
        <Box>
          {mode === DialogMode.EDIT && notice?.noticeId && onDelete && (
            <Button 
              onClick={handleDelete}
              sx={{ 
                backgroundColor: '#EC2D4D', 
                '&:hover': {
                  backgroundColor: '#F26C82'
                }
              }}
            >
              삭제
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CancelButton onClick={handleClose}>닫기</CancelButton>
          <Button 
            onClick={handleSave}
            disabled={isUploading}
          >
            {isUploading ? '업로드 중...' : getSaveButtonText()}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
});

export default NoticeDialog; 